import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { inject, observer } from 'mobx-react';
import { Formik, Form, FormikProps, FormikHelpers } from 'formik';
import merge from 'lodash.merge';
import { PersonType } from 'enums';

import Grid from '@material-ui/core/Grid';
import MenuItem from '@material-ui/core/MenuItem';
import Typography from '@material-ui/core/Typography';

import ConfirmDialog from 'components/ConfirmDialog/ConfirmDialog';
import compose from 'helpers/compose';
import ControlButtons from 'components/ControlButtons';
import FormRadioGroup from 'components/FormRadioGroup';
import FormSelectField from 'components/FormFields/FormSelectField';
import FormTextField from 'components/FormFields/FormTextField';
import Switch from 'components/FormFields/Switch';
import Background from 'components/Layout/Background';
import CompanyOwners from './components/CompanyOwners';
import RequiredDocUpload from 'components/RequiredDocUpload/RequiredDocUpload';

import { Params } from 'pages/Accounts/Edit/accountEditRoutes';
import { RootStore } from 'stores/rootStore';
import { EditAccountStore } from 'stores/editAccountStore';
import { WorkflowAMLStore } from 'stores/workflowStores/workflowAMLStore/workflowAMLStore';
import { AMLData, initialValues } from 'stores/workflowStores/workflowAMLStore/model';

import {
  API_ACCOUNT_CONTRACT_AML_DOC_TYPE,
  API_ACCOUNT_CONTRACT_AML_DOC_SOURCE,
  API_ACCOUNT_CONTRACT_AML_DOC_PEP,
} from 'api/endpoints';
import { ContractTab } from '../Contract';

interface Props {
  editAccountStore?: EditAccountStore;
  rootStore: RootStore;
  tabChange: (tab: ContractTab) => void;
}

const AML: React.FC<Props> = ({ rootStore, tabChange }) => {
  const { accountId } = useParams<Params>();
  const [store] = useState(new WorkflowAMLStore(rootStore, accountId));
  const [legal, setLegal] = useState<boolean | undefined>(undefined);
  const [allowNext, setAllowNext] = useState(true);
  const [notPresentInfo, setNotPresentInfo] = useState(false);
  const { currency } = rootStore.stores.editAccountStore;

  const { t } = useTranslation();

  useEffect(() => {
    (async () => {
      await store.fetchAMLData();
      if (store.data?.personType === PersonType.LEGAL) {
        initialValues.incomeType.type = 'CORPORATE';
      }
      setLegal(store.data !== null && store.data.personType === PersonType.LEGAL);
    })();
  }, [store]);

  const handleSubmit = (values: AMLData, formikHelpers: FormikHelpers<AMLData>) => {
    store.handleNext(tabChange, values, formikHelpers);
  };

  return store.data !== null && legal !== undefined ? (
    <Formik initialValues={merge({}, initialValues, store.data) as AMLData} onSubmit={handleSubmit}>
      {(formikProps: FormikProps<AMLData>) => (
        <Form>
          <Background afterMenu>
            {legal && <CompanyOwners accountId={accountId} />}
            <Background style={{ paddingTop: '32px' }} inside>
              {legal ? (
                <>
                  <Grid container>
                    <Grid item xs={12}>
                      <Typography>{t('contract_aml_legal_branchOfBusiness')}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <FormSelectField name="incomeType.riskBranch" label={t('contract_aml_selectBranchOfBusiness')}>
                        {formikProps.values.options.riskBranch.map((code) => (
                          <MenuItem key={code} value={code}>
                            {t(`contract_aml_riskBranch_${code}`)}
                          </MenuItem>
                        ))}
                      </FormSelectField>
                    </Grid>
                    <Grid item xs={12} />
                    <Grid item xs={12}>
                      <FormRadioGroup
                        name={'revenueInterval'}
                        label={t('contract_aml_legal_revenueInterval')}
                        formikProps={formikProps}
                        values={formikProps.values.options.revenueInterval.map((value) => ({
                          value: value,
                          label: t(`contract_aml_revenueInterval_${value}_${currency}`),
                        }))}
                      />
                    </Grid>
                    <Grid item xs={7}>
                      <FormRadioGroup
                        name={'incomeSource.type'}
                        label={t('contract_aml_incomeSource')}
                        formikProps={formikProps}
                        values={formikProps.values.options.incomeSource.map((value) => ({
                          value: value,
                          label: t(`contract_aml_incomeSource_${value}`),
                        }))}
                        escapeValues={false}
                      />
                    </Grid>
                    {formikProps.values.options.incomeSourceDocRequired.includes(
                      formikProps.values.incomeSource.type,
                    ) && (
                      <RequiredDocUpload
                        accountId={accountId}
                        docTitle={`contract_aml_incomeSource_${formikProps.values.incomeSource.type}_tooltip`}
                        docName="document.source"
                        endpoint={API_ACCOUNT_CONTRACT_AML_DOC_SOURCE}
                        beFile={store.data?.incomeSource.document}
                        formikProps={formikProps}
                      />
                    )}
                  </Grid>
                </>
              ) : (
                <>
                  <Grid container>
                    <Grid item xs={7}>
                      <FormRadioGroup
                        name={'incomeType.type'}
                        label={t('contract_aml_incomeType')}
                        formikProps={formikProps}
                        values={formikProps.values.options.incomeType.map((value) => ({
                          value: value,
                          label: t(`contract_aml_incomeType_${value}`),
                          tooltip: formikProps.values.options.incomeTypeDocRequired.includes(value)
                            ? t(`contract_aml_incomeType_${value}_tooltip`)
                            : undefined,
                        }))}
                        escapeValues={false}
                      />
                    </Grid>
                    <Grid item xs={5}>
                      {formikProps.values.incomeType.type === 'SELF' && (
                        <FormSelectField name="incomeType.riskBranch" label={t('contract_aml_selectBranchOfBusiness')}>
                          {formikProps.values.options.riskBranch.map((code) => (
                            <MenuItem key={code} value={code}>
                              {t(`contract_aml_riskBranch_${code}`)}
                            </MenuItem>
                          ))}
                        </FormSelectField>
                      )}
                    </Grid>
                    {formikProps.values.incomeType.type === 'THIRD_PERSON' && (
                      <Grid item xs={7}>
                        <FormRadioGroup
                          name={'incomeType.thirdPersonType'}
                          label={t('contract_aml_incomeType_thirdPersonType')}
                          formikProps={formikProps}
                          values={formikProps.values.options.thirdPersonIncomeType.map((value) => ({
                            value: value,
                            label: t(`contract_aml_incomeType_thirdPersonType_${value}`),
                          }))}
                        />
                      </Grid>
                    )}
                    {formikProps.values.options.incomeTypeDocRequired.includes(formikProps.values.incomeType.type) && (
                      <RequiredDocUpload
                        accountId={accountId}
                        docTitle={`contract_aml_incomeType_${formikProps.values.incomeType.type}_tooltip`}
                        docName="document.type"
                        endpoint={API_ACCOUNT_CONTRACT_AML_DOC_TYPE}
                        beFile={store.data?.incomeType.document}
                        formikProps={formikProps}
                      />
                    )}
                  </Grid>

                  <Grid container>
                    <Grid item>
                      <FormRadioGroup
                        name={'incomeInterval'}
                        label={t('contract_aml_incomeBracket')}
                        formikProps={formikProps}
                        values={formikProps.values.options.incomeInterval.map((value) => ({
                          value: value,
                          label: t(`contract_aml_incomeBracket_${value}_${currency}`),
                        }))}
                      />
                    </Grid>
                  </Grid>

                  <Grid container>
                    <Grid item xs={7}>
                      <FormRadioGroup
                        name={'incomeSource.type'}
                        label={t('contract_aml_incomeSource')}
                        formikProps={formikProps}
                        values={formikProps.values.options.incomeSource.map((value) => ({
                          value: value,
                          label: t(`contract_aml_incomeSource_${value}`),
                          tooltip: formikProps.values.options.incomeSourceDocRequired.includes(value)
                            ? t(`contract_aml_incomeSource_${value}_tooltip`)
                            : undefined,
                        }))}
                        escapeValues={false}
                      />
                    </Grid>
                    {formikProps.values.options.incomeSourceDocRequired.includes(
                      formikProps.values.incomeSource.type,
                    ) && (
                      <RequiredDocUpload
                        accountId={accountId}
                        docTitle={`contract_aml_incomeSource_${formikProps.values.incomeSource.type}_tooltip`}
                        docName="document.source"
                        endpoint={API_ACCOUNT_CONTRACT_AML_DOC_SOURCE}
                        beFile={store.data?.incomeSource.document}
                        formikProps={formikProps}
                      />
                    )}
                  </Grid>
                </>
              )}

              <Grid container>
                {!legal && (
                  <Grid item xs={12}>
                    <Switch
                      name="pepData.politicallyExposedPerson"
                      label={t(`contract_aml_pep${legal ? '_legal' : ''}`)}
                    />
                  </Grid>
                )}
                {formikProps.values.pepData.politicallyExposedPerson && (
                  <>
                    <Grid item xs={6}>
                      <FormTextField
                        name="pepData.politicallyExposedDetail"
                        label={t(`contract_aml_politicallyExposedDetail${legal ? '_legal' : ''}`)}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      {t('contract_aml_pep_info1')}
                      <ul style={{ width: '60%' }}>
                        <li>{t('contract_aml_pep_opt1')}</li>
                        {!legal && (
                          <>
                            <li>{t('contract_aml_pep_opt2')}</li>
                            <li>{t('contract_aml_pep_opt3')}</li>
                            <li>{t('contract_aml_pep_opt4')}</li>
                          </>
                        )}
                      </ul>
                      {t('contract_aml_pep_info2')}
                    </Grid>
                    {formikProps.values.pepData.politicallyExposedPerson && (
                      <RequiredDocUpload
                        accountId={accountId}
                        docName="document.pep"
                        endpoint={API_ACCOUNT_CONTRACT_AML_DOC_PEP}
                        beFile={store.data?.pepData.document}
                        formikProps={formikProps}
                      />
                    )}
                  </>
                )}
                <Grid item xs={12} />
              </Grid>

              <Grid container>
                <Grid item xs={12}>
                  <Typography>{t(`contract_aml_otherRiskIssues${legal ? '_legal' : ''}`)}</Typography>
                </Grid>
                <Grid item xs={12} />
              </Grid>

              <Grid container>
                <Grid item xs={12}>
                  <Switch name="overLimit" label={t('contract_aml_q_overLimit')} labelWidth="60%" />
                </Grid>
                <Grid item xs={12}>
                  <Switch
                    name="irrationalSelection"
                    label={t('contract_aml_q_irrationalSelection')}
                    labelWidth="60%"
                    afterChange={() => {
                      if (!formikProps.values.irrationalSelection) {
                        formikProps.setFieldValue('irrationalSelectionClient', '');
                        formikProps.setFieldValue('irrationalSelectionActual', '');
                      }
                    }}
                  />
                </Grid>
                {formikProps.values.irrationalSelection && (
                  <>
                    <Grid item xs={9}>
                      <FormTextField
                        name="irrationalSelectionClient"
                        label={t('contract_aml_q_irrationalSelection_client')}
                      />
                    </Grid>
                    <Grid item xs={9}>
                      <FormTextField
                        name="irrationalSelectionActual"
                        label={t('contract_aml_q_irrationalSelection_actual')}
                      />
                    </Grid>
                  </>
                )}
                <Grid item xs={12}>
                  <Switch
                    name="riskTerritory"
                    label={t(`contract_aml_q_riskTerritory${legal ? '_legal' : ''}`)}
                    labelWidth="60%"
                  />
                </Grid>
                {legal && (
                  <>
                    <Grid item xs={12}>
                      <Switch
                        name="riskTerritoryOwner"
                        label={t(`contract_aml_q_riskTerritoryOwner`)}
                        labelWidth="60%"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Switch
                        name="riskTerritoryAssociates"
                        label={t(`contract_aml_q_riskTerritoryAssociates`)}
                        labelWidth="60%"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Switch name="unclearStructure" label={t(`contract_aml_q_unclearStructure`)} labelWidth="60%" />
                    </Grid>
                  </>
                )}
                <Grid item xs={12}>
                  <Switch name="unclearOrigin" label={t('contract_aml_q_unclearOrigin')} labelWidth="60%" />
                </Grid>
                {!legal && (
                  <Grid item xs={12}>
                    <Switch
                      name="thirdPersonSuspicion"
                      label={t('contract_aml_q_thirdPersonSuspicion')}
                      labelWidth="60%"
                    />
                  </Grid>
                )}
                <Grid item xs={12}>
                  <Switch name="wayOfConduct" label={t('contract_aml_q_wayOfConduct')} labelWidth="60%" />
                </Grid>
                <Grid item xs={12}>
                  <Switch
                    name="fundingRiskSuspicion"
                    label={t('contract_aml_q_fundingRiskSuspicion')}
                    labelWidth="60%"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Switch name="fraudSuspicion" label={t('contract_aml_q_fraudSuspicion')} labelWidth="60%" />
                  <ul style={{ width: '60%' }}>
                    <li>{t('contract_aml_q_fraudSuspicion_1')}</li>
                    <li>{t('contract_aml_q_fraudSuspicion_2')}</li>
                    <li>{t('contract_aml_q_fraudSuspicion_3')}</li>
                    <li>{t('contract_aml_q_fraudSuspicion_4')}</li>
                    <li>{t('contract_aml_q_fraudSuspicion_5')}</li>
                    <li>{t('contract_aml_q_fraudSuspicion_6')}</li>
                  </ul>
                </Grid>
                {formikProps.values.fraudSuspicion && (
                  <>
                    {new Array(3).fill(0).map((v, i) => {
                      return (
                        <React.Fragment key={`fraudSuspicionReason${i}`}>
                          <Grid item xs={9}>
                            <FormSelectField
                              name={`fraudSuspicionReasons[${i}]`}
                              label={`${t('contract_aml_q_fraudSuspicion_reason')} ${i + 1}`}
                              options={[{ value: '', label: t(`contract_aml_q_fraudSuspicion_REASON_0`) }].concat(
                                formikProps.values.options.fraudSuspicionReason.map((code, i2) => ({
                                  value: code,
                                  label: t(`contract_aml_q_fraudSuspicion_REASON_${i2 + 1}`),
                                })),
                              )}
                            />
                          </Grid>
                        </React.Fragment>
                      );
                    })}
                    <Grid item xs={9}>
                      <FormTextField
                        name="fraudSuspicionDetail"
                        label={t('contract_aml_q_fraudSuspicion_reasonDetail')}
                        multiline={true}
                        rows={6}
                        maxLength={500}
                      />
                    </Grid>
                  </>
                )}

                <Grid item xs={12}>
                  <Switch
                    name="listedClient"
                    label={t(`contract_aml_q_listedClient${legal ? '_legal' : ''}`)}
                    labelWidth="60%"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Switch
                    name="notPresent"
                    label={t(`contract_aml_q_notPresent`)}
                    labelWidth="60%"
                    afterChange={() => {
                      if (formikProps.values.notPresent) {
                        setAllowNext(false);
                        setNotPresentInfo(true);
                      } else {
                        setAllowNext(true);
                      }
                    }}
                    specificError
                  />
                </Grid>

                <Grid item xs={12}>
                  {t('contract_aml_pep_definition')}
                </Grid>

                <Grid item xs={12}>
                  <span
                    dangerouslySetInnerHTML={{
                      __html: t('contract_aml_q_riskTerritory_info', {
                        interpolation: { escapeValue: false },
                      }),
                    }}
                  />
                </Grid>
              </Grid>
            </Background>

            {notPresentInfo && (
              <ConfirmDialog noHeader centered isOpen={notPresentInfo} confirm={() => setNotPresentInfo(false)}>
                {t('contract_aml_notPresent_info')}
              </ConfirmDialog>
            )}
          </Background>

          <ControlButtons
            isSubmitting={formikProps.isSubmitting}
            nextText={t('contract_additionalInfo')}
            onBack={() => store.handleBack(tabChange)}
            backText={t('contract_personalData')}
            nextDisabled={!allowNext}
          />
        </Form>
      )}
    </Formik>
  ) : null;
};

export default compose(inject('rootStore', 'editAccountStore'), observer)(AML);
